import React from 'react';
import { ElementGroupNames } from '../../../../../common/src/enums/ElementGroupNames';
import { FeatureFlags } from '../../../../../common/src/FeatureFlags';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { fetchDelete, fetchGet } from '../../../store/fetch';
import { openApiFetchPost } from '../../../store/openApiFetch';
import { getFailureCause, getFailureOrigin } from '../../../utils/data/fmecaDataDefinition';
import { hasFeatureFlag } from '../../../utils/hasFeatureFlag';
import {
    CollapsiblePanel,
    EditPanel,
    EditPanelButton,
    PanelBody,
    PanelButtons,
    PanelTable,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    Tray,
} from '../../elements';
import { ComponentSetStatePromised } from '../../utils/ComponentSetStatePromised';
import { showSavedToast } from '../../utils/toastMessages';
import { TotalRiskScoreDot } from '../../utils/TotalRiskScoreDot';
import { SurveyEditCraConditionVerification } from '../CRA/SurveyEditCraConditionVerification';
import SurveyEditVisuals from '../CRA/SurveyEditVisuals';
import { IFailureMode } from '../Interfaces/IFailureMode';
import { AnalysisFailureModeAddEdit } from './FailureModeAnalysisAddEdit';
import { VerificationFailureModeEdit } from './FailureModeVerificationEdit';
import { getUnitFailureModesToCopy } from './getUnitFailureModesToCopy';
import { SurveyEditCopyFailureMode } from './SurveyEditCopyFailureMode';
import { SurveyEditFmecaInspectionFindingVerification } from './SurveyEditFmecaInspectionFindingVerification';

interface ISurveyVerificationProps extends ITranslateProps {
    surveyId: string;
    objectId: string;
    elementId?: string | undefined;
    unitId?: string | undefined;
    elementGroupName?: string | undefined | null;
    manifestationId?: string | undefined;
    failureModeId?: string | undefined;
    selectedName?: string | undefined;
    selectedFailureModeUpdate: (selectedFailureModeId: string | undefined) => void;
    isCraAvailable: boolean;
    isFmecaAvailable: boolean;
    location: {
        query: {
            failureModeId?: string | undefined;
            openCraConditionVerification?: string | undefined;
        };
    };
    objectTreeFilterOnElementGroups: boolean;
    onItemChanged(): void;
}

interface ISurveyVerificationState {
    failureModes: IFailureMode[];
    showAdd: boolean;
    showEdit: boolean;
    unitFailureModes: IFailureMode[];
    shouldShowCopyFailureModeButton: boolean;
    showCopy: boolean;
}

class SurveyVerificationComponent extends ComponentSetStatePromised<ISurveyVerificationProps, ISurveyVerificationState> {
    constructor(props: ISurveyVerificationProps) {
        super(props);

        this.state = {
            failureModes: [],
            showAdd: false,
            showEdit: false,
            unitFailureModes: [],
            showCopy: false,
            shouldShowCopyFailureModeButton: false,
        };
    }

    public override async componentDidMount() {
        await this.loadFailureModes();
        if (this.props.failureModeId) {
            await this.setStatePromised({ showEdit: true, showAdd: false });
            await this.loadUnitFailureModes();
        }
    }

    public override async componentDidUpdate(prevProps: ISurveyVerificationProps) {
        const shouldReload =
            prevProps.elementId !== this.props.elementId ||
            prevProps.unitId !== this.props.unitId ||
            prevProps.manifestationId !== this.props.manifestationId;

        if (shouldReload) {
            await this.setStatePromised({ showAdd: false, showEdit: false });
            await this.loadFailureModes();
            if (this.props.failureModeId) {
                await this.setStatePromised({ showEdit: true, showAdd: false });
            }
            await this.loadUnitFailureModes();
        }
    }

    public override render() {
        return (
            <Tray>
                {this.showCra() && !this.props.manifestationId && (
                    <EditPanel bottomMargin={true} noPadding={true}>
                        <CollapsiblePanel
                            heading={{
                                dark: true,
                                text: `${this.props.t('cra.constructionRiskAssessment')}: ${this.props.selectedName}`,
                                initialIsClosed: this.props.isFmecaAvailable && !this.props.location.query.openCraConditionVerification,
                            }}
                        >
                            <SurveyEditCraConditionVerification
                                {...this.props}
                                onSaved={() => {
                                    showSavedToast(this.props.t);
                                    this.props.onItemChanged();
                                }}
                            />
                            <SurveyEditFmecaInspectionFindingVerification {...this.props} />
                            <SurveyEditVisuals
                                {...this.props}
                                objectTreeFilterOnElementGroups={this.props.objectTreeFilterOnElementGroups}
                                elementGroupName={this.props.elementGroupName}
                            />
                        </CollapsiblePanel>
                    </EditPanel>
                )}

                {this.props.isFmecaAvailable && (
                    <EditPanel noPadding={true}>
                        <CollapsiblePanel heading={{ dark: true, text: this.getHeader(), initialIsClosed: false }}>
                            <PanelBody isForm={true}>
                                <PanelTable hover={true}>
                                    <TableHead>
                                        <TableHeadRow>
                                            <TableHeader>
                                                {this.props.t('common.unit')}/{this.props.t('common.manifestation')}
                                            </TableHeader>
                                            <TableHeader>{this.props.t('failureMode.failureMode')}</TableHeader>
                                            <TableHeader>{this.props.t('failureMode.failureCause')}</TableHeader>
                                            <TableHeader>{this.props.t('failureMode.failureOrigin')}</TableHeader>
                                            <TableHeader>{this.props.t('failureMode.analysisRiskScoreTableHeader')}</TableHeader>
                                            <TableHeader>{this.props.t('failureMode.verificationRiskScoreTableHeader')}</TableHeader>
                                        </TableHeadRow>
                                    </TableHead>
                                    <TableBody>
                                        {this.state.failureModes.map((failureMode) => (
                                            <TableRow
                                                key={failureMode.id}
                                                isRowSelected={failureMode.id === this.props.failureModeId}
                                                onClick={() => this.selectFailureMode(failureMode.id)}
                                            >
                                                <TableData>
                                                    {failureMode.unit.name}{' '}
                                                    {failureMode.manifestation.name ? ` - ${failureMode.manifestation.name}` : ''}
                                                </TableData>
                                                <TableData>
                                                    {failureMode.defaultFailureMode.name
                                                        ? failureMode.defaultFailureMode.name
                                                        : failureMode.customName}
                                                </TableData>
                                                <TableData>{getFailureCause(failureMode.metaData)}</TableData>
                                                <TableData>{getFailureOrigin(failureMode.metaData)}</TableData>
                                                <TableData>
                                                    <TotalRiskScoreDot value={failureMode.analysisRamsWeightedPriority} />{' '}
                                                </TableData>
                                                <TableData>
                                                    <TotalRiskScoreDot value={failureMode.verificationRamsWeightedPriority} />{' '}
                                                </TableData>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </PanelTable>
                                <PanelButtons>
                                    {this.state.shouldShowCopyFailureModeButton && (
                                        <EditPanelButton
                                            text={this.props.t('failureMode.copyFromUnit')}
                                            onClick={async () => this.showCopyFailureMode()}
                                            small
                                        />
                                    )}
                                </PanelButtons>

                                {this.state.showCopy && this.props.manifestationId && (
                                    <SurveyEditCopyFailureMode
                                        unitFailureModes={this.state.unitFailureModes}
                                        surveyId={this.props.surveyId}
                                        manifestationId={this.props.manifestationId}
                                        onCancel={() => this.setState({ showCopy: false })}
                                        onSaved={async () => this.failureModeSaved('add')}
                                    />
                                )}

                                {this.state.showEdit && this.props.failureModeId && (
                                    <VerificationFailureModeEdit
                                        onCancel={() => this.setState({ showEdit: false })}
                                        onSaved={async () => this.failureModeSaved('edit')}
                                        surveyId={this.props.surveyId}
                                        objectId={this.props.objectId}
                                        elementId={this.props.elementId}
                                        unitId={this.props.unitId}
                                        manifestationId={this.props.manifestationId}
                                        failureModeId={this.props.failureModeId}
                                        onRemove={async (id) => this.remove(id)}
                                        objectTreeFilterOnElementGroups={this.props.objectTreeFilterOnElementGroups}
                                        elementGroupName={this.props.elementGroupName}
                                    />
                                )}

                                {this.state.showAdd && this.props.failureModeId && (
                                    <AnalysisFailureModeAddEdit
                                        step="verification"
                                        onCancel={() => this.setState({ showAdd: false })}
                                        onSaved={async (id) => this.failureModeSaved('add', id)}
                                        surveyId={this.props.surveyId}
                                        objectId={this.props.objectId}
                                        elementId={this.props.elementId}
                                        unitId={this.props.unitId}
                                        manifestationId={this.props.manifestationId}
                                        onRemove={async (id) => this.remove(id)}
                                    />
                                )}
                            </PanelBody>
                        </CollapsiblePanel>
                    </EditPanel>
                )}
            </Tray>
        );
    }

    private showCra() {
        return (
            hasFeatureFlag(FeatureFlags.Cra) &&
            this.props.isCraAvailable &&
            (this.props.unitId !== undefined || this.props.manifestationId !== undefined) &&
            this.props.elementGroupName !== undefined &&
            [ElementGroupNames.Foundation, ElementGroupNames.Substructure, ElementGroupNames.Superstructure].includes(
                this.props.elementGroupName as ElementGroupNames,
            )
        );
    }

    private selectFailureMode(id: string) {
        if (this.props.failureModeId === id) {
            this.setState({ showEdit: false, showAdd: true });
            this.props.selectedFailureModeUpdate(undefined);
        } else {
            this.setState({ showEdit: true, showAdd: false });
            this.props.selectedFailureModeUpdate(id);
        }
    }

    private async failureModeSaved(action: 'edit' | 'add', id?: string) {
        if (action === 'edit') {
            void showSavedToast(this.props.t);
        } else {
            this.props.selectedFailureModeUpdate(id);
        }
        await this.setStatePromised({ showEdit: false, showAdd: false, showCopy: false });
        await this.loadFailureModes();
        await this.loadUnitFailureModes();
    }

    private getHeader() {
        if (!this.props.elementId && !this.props.unitId && !this.props.manifestationId) {
            return this.props.t('failureMode.allFailureModes');
        }
        return `${this.props.t('failureMode.failureModes')}: ${this.props.selectedName}`;
    }

    private async loadFailureModes() {
        const url = `/api/surveys/${this.props.surveyId}/failureModes`;

        const result = await fetchGet(url, {
            elementId: this.props.elementId,
            unitId: this.props.unitId,
            manifestationId: this.props.manifestationId,
        });
        await this.setStatePromised({ failureModes: result.json });
    }

    private async remove(id: string) {
        await this.setStatePromised({ showEdit: false, showAdd: false });
        this.props.selectedFailureModeUpdate(undefined);
        await fetchDelete(`/api/surveys/${this.props.surveyId}/failureModes/${id}`);
        await this.loadFailureModes();
    }

    private async loadUnitFailureModes() {
        if (this.props.manifestationId) {
            const failureModesToCopy = await getUnitFailureModesToCopy(
                this.props.surveyId,
                this.props.elementId!,
                this.props.unitId!,
                this.state.failureModes,
            );
            await this.setStatePromised({ shouldShowCopyFailureModeButton: failureModesToCopy.length > 0, unitFailureModes: failureModesToCopy });
        } else {
            await this.setStatePromised({ shouldShowCopyFailureModeButton: false });
        }
    }

    private async showCopyFailureMode() {
        if (this.state.unitFailureModes.length === 1) {
            const unitFailureMode = this.state.unitFailureModes[0]!;
            await openApiFetchPost(
                '/api/surveys/{surveyId}/failureModes/copy',
                { surveyId: this.props.surveyId },
                { manifestationId: this.props.manifestationId!, failureModeId: unitFailureMode.id },
            );
            await this.failureModeSaved('edit');
        } else {
            this.setState({ showEdit: false, showCopy: true });
        }
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const SurveyVerification = translate()(SurveyVerificationComponent);
